// Care Library data layer — fetches the catalog from Medplum and maps the FHIR
// resources into the `Org` shape the browse/detail components render.
//
// The catalog is two resource types, both org-wide (no patient data):
//   - ActivityDefinition (tagged `care-library`) → one per offering
//   - ValueSet (the category axes) → the facets and their categories
//
// We read them straight from the browser MedplumClient (the same path the Care
// Plans tab uses via `useMedplum`), since the catalog is small (~15 offerings)
// and the AccessPolicy grants every clinician read access to both types.
import type { MedplumClient, WithId } from '@medplum/core';
import type {
  ActivityDefinition,
  Bundle,
  Extension,
  ValueSet,
} from '@medplum/fhirtypes';
import { useEffect, useState } from 'react';

import { useMedplum } from '@eluve/feature-patients';

import {
  type Facet,
  type FacetCategory,
  type Offering,
  type OfferingParam,
  type OfferingType,
  type Org,
  TYPE_ORDER,
} from './care-library-data';

const CATALOG_TAG =
  'https://eluve.com/CodeSystem/catalog-membership|care-library';
const OFFERING_TYPE_AXIS = 'offering-type';
const DOSAGE_EXTENSION =
  'https://eluve.com/StructureDefinition/exercise-dosage';
const INSTRUCTION_EXTENSION =
  'https://eluve.com/StructureDefinition/patient-instruction';

// The grouping axes the browse UI renders, in display order. The first is the
// org's primary axis (carries the card swatch + the lead card chip). Each maps
// to a `care-library` ValueSet by canonical URL: the category list and labels
// come from that ValueSet, the per-category counts from the loaded catalog.
const FACET_AXES = ['service-line', 'body-region', 'delivery'] as const;
type FacetAxis = (typeof FACET_AXES)[number];
const PRIMARY_AXIS: FacetAxis = 'service-line';
const FACET_VALUESET_URL: Record<FacetAxis, string> = {
  'service-line': 'https://eluve.com/ValueSet/service-line',
  'body-region': 'https://eluve.com/ValueSet/body-region',
  delivery: 'https://eluve.com/ValueSet/delivery',
};

// Render a clinical code as the catalog's "SYSTEM 12345" face.
const CODE_SYSTEM_LABEL: Record<string, string> = {
  'http://snomed.info/sct': 'SNOMED',
  'http://www.ama-assn.org/go/cpt': 'CPT',
};

const PERIOD_UNIT_LABEL: Record<string, string> = {
  d: 'day',
  wk: 'week',
};

const PAGE_SIZE = 200;
// Belt-and-suspenders page cap so the `next`-link loop always terminates.
const MAX_PAGES = 50;

const isOfferingType = (code: string): code is OfferingType =>
  (TYPE_ORDER as string[]).includes(code);

function formatCode(ad: ActivityDefinition): string | null {
  const coding = ad.code?.coding?.[0];
  if (!coding?.code) return null;
  const label = coding.system ? CODE_SYSTEM_LABEL[coding.system] : undefined;
  return label ? `${label} ${coding.code}` : coding.code;
}

// Every category code an AD carries on a given axis. An offering can sit in
// several categories of one axis, so this returns a list.
function axisCodes(ad: ActivityDefinition, axis: string): string[] {
  return (ad.useContext ?? [])
    .filter((uc) => uc.code?.code === axis)
    .flatMap((uc) => uc.valueCodeableConcept?.coding ?? [])
    .map((coding) => coding.code)
    .filter((code): code is string => Boolean(code));
}

function offeringTypeOf(ad: ActivityDefinition): OfferingType | null {
  const [code] = axisCodes(ad, OFFERING_TYPE_AXIS);
  return code && isOfferingType(code) ? code : null;
}

function extensionByUrl(
  ad: ActivityDefinition,
  url: string,
): Extension | undefined {
  return ad.extension?.find((e) => e.url === url);
}

function nestedInt(ext: Extension, url: string): number | undefined {
  return ext.extension?.find((e) => e.url === url)?.valueInteger;
}

// Reconstruct the exercise parameter grid (sets / reps / hold / frequency) from
// the `exercise-dosage` extension and `timingTiming`. Non-exercise types carry
// no structured parameters in FHIR — the detail view falls back to the `usage`
// line and patient instructions for them.
function paramsOf(ad: ActivityDefinition): OfferingParam[] {
  const dosage = extensionByUrl(ad, DOSAGE_EXTENSION);
  if (!dosage) return [];

  const sets = nestedInt(dosage, 'sets');
  const reps = nestedInt(dosage, 'reps');
  const hold = nestedInt(dosage, 'holdSeconds');

  const repeat = ad.timingTiming?.repeat;
  const frequency =
    repeat?.frequency && repeat.periodUnit
      ? `${repeat.frequency}×/${PERIOD_UNIT_LABEL[repeat.periodUnit] ?? repeat.periodUnit}`
      : undefined;

  const params: OfferingParam[] = [];
  if (sets !== undefined) {
    params.push({ label: 'Sets', value: String(sets), mono: true });
  }
  if (reps !== undefined) {
    params.push({ label: 'Reps', value: String(reps), mono: true });
  }
  if (hold !== undefined) {
    params.push({ label: 'Hold', value: `${hold}s`, mono: true });
  }
  if (frequency) {
    params.push({ label: 'Frequency', value: frequency, mono: true });
  }
  return params;
}

function instructionOf(ad: ActivityDefinition): string | null {
  return extensionByUrl(ad, INSTRUCTION_EXTENSION)?.valueString ?? null;
}

// An AD without a recognized offering-type axis can't be placed in the type
// filter, so it's dropped (returns null) rather than rendered untyped.
function adToOffering(ad: WithId<ActivityDefinition>): Offering | null {
  const type = offeringTypeOf(ad);
  if (!type) return null;

  const cats: Record<string, string[]> = {};
  for (const axis of FACET_AXES) {
    const codes = axisCodes(ad, axis);
    if (codes.length > 0) cats[axis] = codes;
  }

  return {
    id: ad.id,
    type,
    name: ad.title ?? ad.name ?? 'Untitled offering',
    code: formatCode(ad),
    meta: ad.usage ?? '',
    cats,
    desc: ad.description ?? '',
    params: paramsOf(ad),
    instructions: instructionOf(ad),
  };
}

// Strip the "Care Library — " prefix the ValueSet titles carry so the facet
// header reads "Service line", not "Care Library — Service line".
function facetName(vs: ValueSet, axis: string): string {
  return (vs.title ?? vs.name ?? axis).replace(/^Care Library\s*[—–-]\s*/, '');
}

function valueSetToFacet(
  vs: ValueSet,
  axis: FacetAxis,
  items: Offering[],
): Facet {
  const cats: FacetCategory[] = (vs.compose?.include ?? [])
    .flatMap((include) => include.concept ?? [])
    .map((concept) => ({
      id: concept.code,
      name: concept.display ?? concept.code,
      count: items.filter((it) => (it.cats[axis] ?? []).includes(concept.code))
        .length,
    }));
  // Keep `primary` an optional flag — present only on the primary axis — so the
  // shape matches how the facet was authored (truthy check, never `false`).
  return {
    id: axis,
    name: facetName(vs, axis),
    primary: axis === PRIMARY_AXIS || undefined,
    cats,
  };
}

function buildOrg(
  ads: WithId<ActivityDefinition>[],
  valueSets: ValueSet[],
): Org {
  const items = ads
    .map(adToOffering)
    .filter((offering): offering is Offering => offering !== null);

  const vsByUrl = new Map(
    valueSets
      .filter((vs): vs is ValueSet & { url: string } => Boolean(vs.url))
      .map((vs) => [vs.url, vs]),
  );

  const facets: Facet[] = [];
  for (const axis of FACET_AXES) {
    const vs = vsByUrl.get(FACET_VALUESET_URL[axis]);
    if (vs) facets.push(valueSetToFacet(vs, axis, items));
  }

  const name = ads.find((ad) => ad.publisher)?.publisher ?? 'Care Library';
  return { id: 'care-library', name, facets, items };
}

async function fetchCatalogActivityDefinitions(
  client: MedplumClient,
): Promise<WithId<ActivityDefinition>[]> {
  const results: WithId<ActivityDefinition>[] = [];
  const qs = new URLSearchParams();
  qs.append('_tag', CATALOG_TAG);
  qs.append('_count', String(PAGE_SIZE));
  qs.append('_sort', 'title');

  const ingest = (bundle: Bundle<ActivityDefinition>) => {
    for (const entry of bundle.entry ?? []) {
      const r = entry.resource;
      if (r?.id) results.push(r as WithId<ActivityDefinition>);
    }
    return bundle.link?.find((l) => l.relation === 'next')?.url;
  };

  let nextUrl = ingest(
    (await client.search(
      'ActivityDefinition',
      qs.toString(),
    )) as Bundle<ActivityDefinition>,
  );
  let pages = 1;
  while (nextUrl && pages < MAX_PAGES) {
    const bundle = (await client.get(nextUrl)) as Bundle<ActivityDefinition>;
    nextUrl = ingest(bundle);
    pages += 1;
  }
  return results;
}

async function fetchFacetValueSets(client: MedplumClient): Promise<ValueSet[]> {
  const qs = new URLSearchParams();
  qs.append(
    'url',
    FACET_AXES.map((axis) => FACET_VALUESET_URL[axis]).join(','),
  );
  qs.append('_count', String(PAGE_SIZE));
  const bundle = (await client.search(
    'ValueSet',
    qs.toString(),
  )) as Bundle<ValueSet>;
  return (bundle.entry ?? [])
    .map((entry) => entry.resource)
    .filter((r): r is ValueSet => Boolean(r));
}

export async function fetchCareLibrary(client: MedplumClient): Promise<Org> {
  const [ads, valueSets] = await Promise.all([
    fetchCatalogActivityDefinitions(client),
    fetchFacetValueSets(client),
  ]);
  return buildOrg(ads, valueSets);
}

export interface CareLibraryResult {
  /** The catalog, or `null` while loading / when Medplum is unavailable. */
  org: Org | null;
  isLoading: boolean;
  isError: boolean;
  /** Whether Medplum is enabled for this org — gates the disabled message. */
  enabled: boolean;
}

export function useCareLibrary(): CareLibraryResult {
  const { client, enabled } = useMedplum();
  const [org, setOrg] = useState<Org | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!client) {
      setOrg(null);
      setIsLoading(false);
      setIsError(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setIsError(false);
    fetchCareLibrary(client)
      .then((result) => {
        if (!cancelled) setOrg(result);
      })
      .catch(() => {
        if (!cancelled) setIsError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  return { org, isLoading, isError, enabled };
}
